import { useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Sparkles, Brain } from "lucide-react";
import { toast } from "sonner";

const PERIODS = [
  { value: "today", label: "Hari Ini" },
  { value: "week", label: "7 Hari" },
  { value: "month", label: "30 Hari" },
  { value: "year", label: "1 Tahun" },
];

export default function AIInsights() {
  const [period, setPeriod] = useState("month");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const analyze = async () => {
    setLoading(true);
    setResult(null);
    try {
      const r = await api.get("/ai/insights", { params: { period } });
      setResult(r.data);
    } catch {
      toast.error("Gagal memanggil AI. Coba lagi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1.5">
          <Sparkles className="h-3 w-3 text-primary" /> Analisa Cerdas
        </p>
        <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">AI Insight</h1>
        <p className="mt-1 text-sm text-muted-foreground">Analisa keuntungan, deteksi anomali, dan prediksi kebutuhan saldo · powered by Claude Sonnet.</p>
      </div>

      <Card className="p-5 bg-gradient-to-br from-primary/5 via-purple-500/5 to-transparent border-primary/20">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-primary text-primary-foreground grid place-items-center">
            <Brain className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <p className="font-semibold">Analisa performa usaha Anda</p>
            <p className="text-xs text-muted-foreground">Pilih periode dan biarkan AI meninjau data Anda.</p>
          </div>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <Tabs value={period} onValueChange={setPeriod} className="flex-1">
            <TabsList className="w-full">
              {PERIODS.map((p) => <TabsTrigger key={p.value} value={p.value} data-testid={`ai-period-${p.value}`} className="flex-1">{p.label}</TabsTrigger>)}
            </TabsList>
          </Tabs>
          <Button data-testid="btn-run-ai" onClick={analyze} disabled={loading} className="gap-2 sm:min-w-[160px]">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Menganalisa...</> : <><Sparkles className="h-4 w-4" /> Jalankan Analisa</>}
          </Button>
        </div>
      </Card>

      {result && (
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4 pb-4 border-b">
            <Sparkles className="h-4 w-4 text-primary" />
            <p className="text-sm font-semibold">Hasil Analisa AI · periode {result.period}</p>
          </div>
          <MarkdownLite text={result.analysis} />
        </Card>
      )}
    </div>
  );
}

function MarkdownLite({ text }) {
  // Simple markdown renderer for h3, bold, list, paragraph
  const lines = String(text || "").split("\n");
  return (
    <div className="prose prose-sm max-w-none dark:prose-invert space-y-3">
      {lines.map((l, i) => {
        const line = l.trim();
        if (!line) return null;
        if (line.startsWith("### ")) return <h3 key={i} className="text-base font-bold mt-4">{line.slice(4)}</h3>;
        if (line.startsWith("## ")) return <h2 key={i} className="text-lg font-bold mt-4">{line.slice(3)}</h2>;
        if (line.startsWith("# ")) return <h1 key={i} className="text-xl font-bold mt-4">{line.slice(2)}</h1>;
        if (line.startsWith("- ") || line.startsWith("* ")) return <li key={i} className="ml-5 list-disc text-sm">{bold(line.slice(2))}</li>;
        return <p key={i} className="text-sm leading-relaxed">{bold(line)}</p>;
      })}
    </div>
  );
}
function bold(s) {
  const parts = s.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => (p.startsWith("**") && p.endsWith("**")) ? <strong key={i}>{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>);
}
